import { lazy, Suspense } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";

const TradeStage = lazy(() =>
  import("~/three/TradeStage").then((m) => ({ default: m.TradeStage })),
);

export function Trade() {
  const face = FACE_BY_ID.trade;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-56 w-full overflow-hidden border-b border-jg-border/40 sm:h-72">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-trade/15 to-transparent"
            />
          }
        >
          <TradeStage />
        </Suspense>

        <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 text-xs sm:left-6">
          <span
            className="inline-block size-2 animate-pulse rounded-full"
            style={{ backgroundColor: face.themeColor }}
            aria-hidden="true"
          />
          <span className="font-mono uppercase tracking-[0.25em] text-jg-fg-muted">
            Live · illustrative
          </span>
        </div>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          <span style={{ color: face.themeColor }}>Fintech</span> &amp;
          automation, end to end
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          Trading-terminal UX, websocket market data, rule engines, and the
          compliance plumbing underneath.
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Featured product
            </p>
            <h2 className="mt-2 font-display text-xl font-semibold">
              Cypher Bot
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted">
              In-progress trading SaaS — automation rules, market data
              integration, terminal-style UI. Dark techno aesthetic, real
              broker pipes underneath.
            </p>
            <a
              href="https://app.cypher-bot.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-opacity hover:opacity-90"
              style={{
                backgroundColor: face.themeColor,
                color: "#0a0a0f",
              }}
            >
              Open Cypher Bot
              <span aria-hidden="true">↗</span>
            </a>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              What we build
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">Automation:</span> rule engines,
                event-driven workers, backtesting harnesses.
              </li>
              <li>
                <span className="text-jg-fg">Market data:</span> websocket
                feeds, OHLC normalization, latency-aware caching.
              </li>
              <li>
                <span className="text-jg-fg">UX:</span> trading-terminal
                interfaces, real-time dashboards, alerting flows.
              </li>
              <li>
                <span className="text-jg-fg">Compliance:</span> KYC handoffs,
                audit-friendly logging, secrets discipline.
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h3
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Shipped strategies
          </h3>
          <ul className="grid gap-3">
            <li className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="font-display text-lg font-semibold leading-tight">
                  ATR Trailing Stop com EMA
                </h4>
                <span
                  className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em]"
                  style={{ color: face.themeColor }}
                >
                  Nelogica · 2025
                </span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted">
                Published on the Nelogica Profit Pro marketplace. EMA + ATR
                Trailing Stop with adjustable sensitivity. Buy when price
                closes above the trailing stop and the EMA crosses up;
                sell on the mirror condition. Optional Heikin Ashi
                smoothing, blue/red bar coloring on triggers, real-time
                alerts. Aimed at trend-reversal detection on volatile
                instruments.
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {["NTSL", "Profit Pro", "Trading", "Nelogica"].map((t) => (
                  <li
                    key={t}
                    className="rounded-full border border-jg-border bg-jg-bg/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-jg-fg-muted"
                  >
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href="https://nelogica.com.br/estrategias?id=10748"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: face.themeColor,
                    color: "#0a0a0f",
                  }}
                >
                  Open on Nelogica
                  <span aria-hidden="true">↗</span>
                </a>
                <a
                  href="https://www.youtube.com/watch?v=jHufv5JkHyw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-jg-border bg-jg-bg-soft/60 px-3 py-1 text-sm text-jg-fg-muted hover:text-jg-fg"
                >
                  Watch demo
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-dashed border-jg-border bg-jg-bg-soft/30 p-5 text-sm text-jg-fg-muted">
          <strong className="block font-display text-jg-fg">
            About the chart above
          </strong>
          <p className="mt-2">
            96 instanced candlesticks rendered in WebGL via{" "}
            <code className="rounded bg-jg-bg-soft/60 px-1 py-0.5 text-xs">
              InstancedMesh
            </code>{" "}
            (~1 draw call per direction). The series is a seeded Mulberry32
            random walk — stable per release, illustrative only. Real
            production charts wire to broker websocket feeds with the same
            instanced rendering pattern.
          </p>
        </div>
      </div>
    </section>
  );
}
